import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Users,
  Mail,
  Phone,
  MessageSquare,
  Linkedin,
  DollarSign,
  Zap,
  Eye,
  MousePointer,
  Reply,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Settings,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Award,
  Layers,
  PieChart,
  LineChart,
  BarChart,
  Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { FunnelIcon } from '../../components/ui/Icon/Icon';
import { cn } from '../../utils/cn';

// ============================================================================
// INTERFACES
// ============================================================================

interface CampaignMetrics {
  campaignId: string;
  campaignName: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  channels: string[];
  totalSent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  connected: number;
  converted: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  conversionRate: number;
  leadsGenerated: number;
  meetingsScheduled: number;
  revenue: number;
  costPerLead: number;
  roi: number;
  engagementScore: number;
  qualityScore: number;
  trendDirection: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface ChannelPerformance {
  channel: string;
  sent: number;
  conversionRate: number;
  revenue: number;
  cost: number;
  roi: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
}

interface FunnelStage {
  name: string;
  contacts: number;
  conversionRate: number;
  dropoffRate: number;
  averageTime: number;
}

interface CohortData {
  cohortDate: string;
  cohortSize: number;
  retentionRates: { [key: string]: number };
  revenueData: { [key: string]: number };
}

interface CampaignAnalyticsDashboardProps {
  className?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CampaignAnalyticsDashboard: React.FC<CampaignAnalyticsDashboardProps> = ({ className }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'funnel' | 'cohort' | 'benchmark' | 'custom'>('overview');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['email', 'sms', 'linkedin', 'voicemail']);
  
  // Data states
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [channelPerformance, setChannelPerformance] = useState<ChannelPerformance[]>([]);
  const [funnelData, setFunnelData] = useState<{ stages: FunnelStage[], overallConversion: number }>({ stages: [], overallConversion: 0 });
  const [cohortData, setCohortData] = useState<CohortData[]>([]);
  const [realTimeInsights, setRealTimeInsights] = useState<any>(null);

  // Mock data for demonstration
  const mockDashboardData = {
    summary: {
      totalCampaigns: 15,
      activeCampaigns: 8,
      totalContacts: 125000,
      totalSent: 89000,
      overallConversionRate: 4.2,
      totalRevenue: 285000,
      avgROI: 340,
      engagementScore: 78.5
    },
    topCampaigns: [
      {
        campaignId: 'camp-001',
        campaignName: 'Q1 Product Launch',
        status: 'active' as const,
        channels: ['email', 'sms', 'linkedin'],
        totalSent: 15420,
        delivered: 14891,
        opened: 4467,
        clicked: 892,
        replied: 234,
        connected: 156,
        converted: 87,
        deliveryRate: 96.5,
        openRate: 30.0,
        clickRate: 20.0,
        replyRate: 5.2,
        conversionRate: 5.6,
        leadsGenerated: 87,
        meetingsScheduled: 34,
        revenue: 95000,
        costPerLead: 125,
        roi: 475,
        engagementScore: 82.3,
        qualityScore: 88.7,
        trendDirection: 'up' as const,
        trendPercentage: 15.2
      },
      {
        campaignId: 'camp-002',
        campaignName: 'Enterprise Outreach',
        status: 'active' as const,
        channels: ['email', 'linkedin'],
        totalSent: 8650,
        delivered: 8347,
        opened: 2421,
        clicked: 485,
        replied: 142,
        connected: 89,
        converted: 52,
        deliveryRate: 96.5,
        openRate: 29.0,
        clickRate: 20.0,
        replyRate: 5.9,
        conversionRate: 6.0,
        leadsGenerated: 52,
        meetingsScheduled: 23,
        revenue: 78000,
        costPerLead: 156,
        roi: 390,
        engagementScore: 79.1,
        qualityScore: 85.2,
        trendDirection: 'up' as const,
        trendPercentage: 8.7
      }
    ],
    channelBreakdown: [
      { channel: 'Email', sent: 45000, conversionRate: 3.8, revenue: 120000, cost: 8500, roi: 1312, trend: 'up' as const, trendPercentage: 12.4 },
      { channel: 'SMS', sent: 25000, conversionRate: 5.2, revenue: 89000, cost: 7500, roi: 1087, trend: 'up' as const, trendPercentage: 18.9 },
      { channel: 'LinkedIn', sent: 15000, conversionRate: 2.9, revenue: 45000, cost: 3000, roi: 1400, trend: 'stable' as const, trendPercentage: 2.1 },
      { channel: 'Voicemail', sent: 4000, conversionRate: 7.1, revenue: 31000, cost: 2000, roi: 1450, trend: 'up' as const, trendPercentage: 25.3 }
    ]
  };

  const mockFunnelData = {
    stages: [
      { name: 'Sent', contacts: 89000, conversionRate: 100, dropoffRate: 0, averageTime: 0 },
      { name: 'Delivered', contacts: 85630, conversionRate: 96.2, dropoffRate: 3.8, averageTime: 1 },
      { name: 'Opened', contacts: 25689, conversionRate: 30.0, dropoffRate: 70.0, averageTime: 24 },
      { name: 'Clicked', contacts: 5138, conversionRate: 20.0, dropoffRate: 80.0, averageTime: 48 },
      { name: 'Replied', contacts: 1283, conversionRate: 5.0, dropoffRate: 95.0, averageTime: 72 },
      { name: 'Converted', contacts: 374, conversionRate: 4.2, dropoffRate: 95.8, averageTime: 168 }
    ],
    overallConversion: 4.2
  };

  const mockCohortData = [
    { cohortDate: '2024-01-01', cohortSize: 1000, retentionRates: { day1: 100, day7: 85, day30: 72, day90: 58 }, revenueData: { day1: 5000, day7: 12000, day30: 28000, day90: 45000 } },
    { cohortDate: '2024-02-01', cohortSize: 1200, retentionRates: { day1: 100, day7: 88, day30: 75, day90: 62 }, revenueData: { day1: 6000, day7: 14000, day30: 32000, day90: 52000 } },
    { cohortDate: '2024-03-01', cohortSize: 1500, retentionRates: { day1: 100, day7: 90, day30: 78, day90: 0 }, revenueData: { day1: 7500, day7: 18000, day30: 38000, day90: 0 } }
  ];

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setDashboardData(mockDashboardData);
      setChannelPerformance(mockDashboardData.channelBreakdown);
      setFunnelData(mockFunnelData);
      setCohortData(mockCohortData);
      setRealTimeInsights({
        lastUpdated: new Date(),
        alerts: [],
        trending: ['conversion rate', 'email opens'],
        recommendations: [
          'Consider increasing SMS volume based on high conversion rates',
          'A/B test LinkedIn connection messages',
          'Email open rates trending up - scale successful campaigns'
        ]
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, selectedCampaigns, selectedChannels]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const getTrendIcon = (direction: 'up' | 'down' | 'stable', size = 16) => {
    switch (direction) {
      case 'up': return <TrendingUp size={size} className="text-green-500" />;
      case 'down': return <TrendingDown size={size} className="text-red-500" />;
      default: return <Minus size={size} className="text-gray-500" />;
    }
  };

  const getChannelIcon = (channel: string, size = 20) => {
    switch (channel.toLowerCase()) {
      case 'email': return <Mail size={size} className="text-blue-500" />;
      case 'sms': return <MessageSquare size={size} className="text-green-500" />;
      case 'linkedin': return <Linkedin size={size} className="text-blue-600" />;
      case 'voicemail': return <Phone size={size} className="text-purple-500" />;
      default: return <Activity size={size} className="text-gray-500" />;
    }
  };

  const formatNumber = (num: number, decimals = 0) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(decimals)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(decimals)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number, decimals = 1) => {
    return `${value.toFixed(decimals)}%`;
  };

  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-neutral-400">Loading campaign analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-neutral-300 bg-clip-text text-transparent">
            Campaign Analytics
          </h1>
          <p className="text-neutral-400 mt-1">
            Unified performance insights across all channels
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={loadDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>

          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>

          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-neutral-800/50 rounded-lg p-1">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'performance', label: 'Performance', icon: TrendingUp },
          { id: 'funnel', label: 'Conversion Funnel', icon: FunnelIcon },
          { id: 'cohort', label: 'Cohort Analysis', icon: Users },
          { id: 'benchmark', label: 'Benchmarks', icon: Award },
          { id: 'custom', label: 'Custom Metrics', icon: Sparkles }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
              activeTab === tab.id
                ? "bg-blue-600 text-white shadow-lg"
                : "text-neutral-400 hover:text-white hover:bg-neutral-700"
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-200 text-sm font-medium">Total Campaigns</p>
                        <p className="text-3xl font-bold text-white mt-1">
                          {dashboardData?.summary.totalCampaigns}
                        </p>
                        <p className="text-blue-300 text-sm mt-1">
                          {dashboardData?.summary.activeCampaigns} active
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Target className="w-6 h-6 text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-200 text-sm font-medium">Conversion Rate</p>
                        <p className="text-3xl font-bold text-white mt-1">
                          {formatPercentage(dashboardData?.summary.overallConversionRate)}
                        </p>
                        <div className="flex items-center mt-1">
                          <TrendingUp size={14} className="text-green-400 mr-1" />
                          <p className="text-green-300 text-sm">+12.5%</p>
                        </div>
                      </div>
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-green-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-200 text-sm font-medium">Total Revenue</p>
                        <p className="text-3xl font-bold text-white mt-1">
                          {formatCurrency(dashboardData?.summary.totalRevenue)}
                        </p>
                        <div className="flex items-center mt-1">
                          <TrendingUp size={14} className="text-purple-400 mr-1" />
                          <p className="text-purple-300 text-sm">+8.9%</p>
                        </div>
                      </div>
                      <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-purple-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-900/50 to-orange-800/30 border-orange-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-200 text-sm font-medium">Average ROI</p>
                        <p className="text-3xl font-bold text-white mt-1">
                          {dashboardData?.summary.avgROI}%
                        </p>
                        <div className="flex items-center mt-1">
                          <TrendingUp size={14} className="text-orange-400 mr-1" />
                          <p className="text-orange-300 text-sm">+15.2%</p>
                        </div>
                      </div>
                      <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <Zap className="w-6 h-6 text-orange-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Channel Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Layers className="w-5 h-5" />
                    <span>Channel Performance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {channelPerformance.map((channel, index) => (
                      <motion.div
                        key={channel.channel}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-lg border border-neutral-700 bg-neutral-800/50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            {getChannelIcon(channel.channel)}
                            <span className="font-medium text-white">{channel.channel}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {getTrendIcon(channel.trend, 16)}
                            <span className="text-sm text-neutral-400">
                              {formatPercentage(channel.trendPercentage)}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-neutral-400">Sent</p>
                            <p className="text-white font-medium">{formatNumber(channel.sent)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-400">Conversion</p>
                            <p className="text-white font-medium">{formatPercentage(channel.conversionRate)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-400">Revenue</p>
                            <p className="text-white font-medium">{formatCurrency(channel.revenue)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-400">ROI</p>
                            <p className="text-white font-medium">{channel.roi}%</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performing Campaigns */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Award className="w-5 h-5" />
                    <span>Top Performing Campaigns</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dashboardData?.topCampaigns.map((campaign: CampaignMetrics, index: number) => (
                      <motion.div
                        key={campaign.campaignId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-6 rounded-lg border border-neutral-700 bg-gradient-to-r from-neutral-800/50 to-neutral-900/30"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-white">{campaign.campaignName}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={cn(
                                "px-2 py-1 rounded text-xs font-medium",
                                campaign.status === 'active' ? "bg-green-500/20 text-green-400" :
                                campaign.status === 'paused' ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-gray-500/20 text-gray-400"
                              )}>
                                {campaign.status}
                              </span>
                              <div className="flex items-center space-x-1">
                                {campaign.channels.map((channel) => (
                                  <div key={channel} className="flex items-center">
                                    {getChannelIcon(channel, 14)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getTrendIcon(campaign.trendDirection)}
                            <span className="text-sm text-neutral-400">
                              {formatPercentage(campaign.trendPercentage)}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <p className="text-neutral-400 text-sm">Conversion Rate</p>
                            <p className="text-xl font-bold text-white">{formatPercentage(campaign.conversionRate)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-400 text-sm">Revenue</p>
                            <p className="text-xl font-bold text-white">{formatCurrency(campaign.revenue)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-400 text-sm">ROI</p>
                            <p className="text-xl font-bold text-white">{campaign.roi}%</p>
                          </div>
                          <div>
                            <p className="text-neutral-400 text-sm">Engagement Score</p>
                            <p className="text-xl font-bold text-white">{formatPercentage(campaign.engagementScore, 0)}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Real-time Insights */}
              {realTimeInsights && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Activity className="w-5 h-5" />
                      <span>Real-time Insights</span>
                      <span className="text-xs text-neutral-400">
                        Updated {new Date(realTimeInsights.lastUpdated).toLocaleTimeString()}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-neutral-300 mb-2">Trending Metrics</h4>
                        <div className="flex flex-wrap gap-2">
                          {realTimeInsights.trending.map((metric: string, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm flex items-center space-x-1"
                            >
                              <TrendingUp size={12} />
                              <span>{metric}</span>
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-neutral-300 mb-2">Recommendations</h4>
                        <div className="space-y-2">
                          {realTimeInsights.recommendations.map((rec: string, index: number) => (
                            <div key={index} className="flex items-start space-x-2 text-sm text-neutral-400">
                              <Sparkles size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>{rec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-400">
                    Detailed performance analysis coming soon...
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'funnel' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <FunnelIcon className="w-5 h-5" />
                    <span>Conversion Funnel Analysis</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Funnel Overview */}
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">
                        {formatPercentage(funnelData.overallConversion)}
                      </p>
                      <p className="text-neutral-400">Overall Conversion Rate</p>
                    </div>

                    {/* Funnel Stages */}
                    <div className="space-y-4">
                      {funnelData.stages.map((stage, index) => (
                        <motion.div
                          key={stage.name}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="relative"
                        >
                          <div className="flex items-center justify-between p-4 rounded-lg border border-neutral-700 bg-neutral-800/50">
                            <div className="flex items-center space-x-4">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {index + 1}
                              </div>
                              <div>
                                <h3 className="text-white font-medium">{stage.name}</h3>
                                <p className="text-sm text-neutral-400">
                                  {formatNumber(stage.contacts)} contacts
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">
                                {formatPercentage(stage.conversionRate)}
                              </p>
                              {stage.dropoffRate > 0 && (
                                <p className="text-sm text-red-400">
                                  {formatPercentage(stage.dropoffRate)} drop-off
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress bar */}
                          <div className="w-full bg-neutral-700 h-2 rounded-full mt-2">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${stage.conversionRate}%` }}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'cohort' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>Cohort Analysis</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cohortData.map((cohort, index) => (
                      <motion.div
                        key={cohort.cohortDate}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-lg border border-neutral-700 bg-neutral-800/50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-white font-medium">
                              Cohort {new Date(cohort.cohortDate).toLocaleDateString()}
                            </h3>
                            <p className="text-sm text-neutral-400">
                              {formatNumber(cohort.cohortSize)} contacts
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-neutral-400">Day 1</p>
                            <p className="text-white font-medium">
                              {formatPercentage(cohort.retentionRates.day1)}
                            </p>
                          </div>
                          <div>
                            <p className="text-neutral-400">Day 7</p>
                            <p className="text-white font-medium">
                              {formatPercentage(cohort.retentionRates.day7)}
                            </p>
                          </div>
                          <div>
                            <p className="text-neutral-400">Day 30</p>
                            <p className="text-white font-medium">
                              {formatPercentage(cohort.retentionRates.day30)}
                            </p>
                          </div>
                          {cohort.retentionRates.day90 && (
                            <div>
                              <p className="text-neutral-400">Day 90</p>
                              <p className="text-white font-medium">
                                {formatPercentage(cohort.retentionRates.day90)}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'benchmark' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Award className="w-5 h-5" />
                    <span>Performance Benchmarks</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-400">
                    Performance benchmarking coming soon...
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'custom' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Sparkles className="w-5 h-5" />
                    <span>Custom Metrics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-400">
                    Custom metrics builder coming soon...
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default CampaignAnalyticsDashboard; 